import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Autocomplete, Box, Button, TextField } from "@mui/material";

import { useCountryListing } from "../../../../api/queries/network/useCountryListing";

import { userRoleList } from "../../../User/enums";

import {
  serviceAvailabilityList,
  serviceCategoryList,
  serviceDeliveryList,
  serviceTypesList,
} from "../../enum/service.enum";

const NetworkFilters = ({ isDataLoading }: { isDataLoading: boolean }) => {
  const { t } = useTranslation("goService", { keyPrefix: "trendingServicesPage" });
  const { t: tCommon } = useTranslation("common", { keyPrefix: "user.role" });
  const { t: tService } = useTranslation("goService", { keyPrefix: "serviceType" });
  const { t: tCategory } = useTranslation("goService", { keyPrefix: "serviceCategory" });
  const { t: tServiceAvailability } = useTranslation("goService", { keyPrefix: "serviceAvailability" });
  const [searchParams, setSearchParams] = useSearchParams();
  const { countries, isCountriesDataLoading } = useCountryListing();
  const userRoles = userRoleList.map((role) => ({ label: tCommon(role), value: role }));
  const serviceTypes = serviceTypesList.map((type) => ({ label: tService(type), value: type }));
  const serviceCategories = serviceCategoryList.map((category) => ({ label: tCategory(category), value: category }));
  const ratings = ["1", "2", "3", "4", "5"].map((rating) => ({
    label: t("star", { count: Number(rating) }),
    value: rating,
  }));
  const serviceAvailability = serviceAvailabilityList.map((availability) => ({
    label: tServiceAvailability(availability),
    value: availability,
  }));

  // Get values from URL params
  const roleValue = searchParams.get("role") || null;
  const countryValue = searchParams.get("country") ? Number(searchParams.get("country")) : null;
  const typeValue = searchParams.get("type") || null;
  const categoryValue = searchParams.get("category") || null;
  const ratingValue = searchParams.get("rating") || null;
  const availabilityValue = searchParams.get("availability") || null;

  // Find selected options
  const selectedRole = roleValue ? userRoles.find((role) => role.value === roleValue) || null : null;
  const selectedCountry = countryValue
    ? countries.find((country: { value: number }) => country.value === countryValue) || null
    : null;
  const selectedType = typeValue ? serviceTypes.find((type) => type.value === typeValue) || null : null;
  const selectedCategory = categoryValue ? serviceCategories.find((cat) => cat.value === categoryValue) || null : null;
  const selectedRating = ratingValue ? ratings.find((rating) => rating.value === ratingValue) || null : null;
  const selectedAvailability = availabilityValue
    ? serviceAvailability.find((availability) => availability.value === availabilityValue) || null
    : null;

  // Update URL params handlers
  const updateFilter = (key: string, value: string | number | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value.toString());
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const clearAllFilters = () => setSearchParams(new URLSearchParams());

  return (
    <Box sx={styles.container}>
      <Box sx={styles.filtersWrapper}>
        <Autocomplete
          disablePortal
          disabled={isDataLoading}
          options={userRoles}
          value={selectedRole}
          onChange={(_, newValue) => {
            updateFilter("role", newValue?.value || null);
          }}
          sx={styles.autocomplete("150px")}
          renderInput={(params) => <TextField {...params} placeholder={t("role")} />}
        />
        <Autocomplete
          disablePortal
          disabled={isDataLoading || isCountriesDataLoading}
          options={countries}
          value={selectedCountry}
          onChange={(_, newValue) => {
            updateFilter("country", newValue?.value || null);
          }}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          sx={styles.autocomplete("150px")}
          renderInput={(params) => <TextField {...params} placeholder={t("country")} />}
        />
        <Autocomplete
          disablePortal
          disabled={isDataLoading}
          options={serviceTypes}
          value={selectedType}
          onChange={(_, newValue) => {
            updateFilter("type", newValue?.value || null);
          }}
          sx={styles.autocomplete("150px")}
          renderInput={(params) => <TextField {...params} placeholder={t("type")} />}
        />
        <Autocomplete
          disablePortal
          disabled={isDataLoading}
          options={serviceCategories}
          value={selectedCategory}
          onChange={(_, newValue) => {
            updateFilter("category", newValue?.value || null);
          }}
          sx={styles.autocomplete("150px")}
          renderInput={(params) => <TextField {...params} placeholder={t("categories")} />}
        />
        <Autocomplete
          disablePortal
          disabled={isDataLoading}
          options={ratings}
          value={selectedRating}
          onChange={(_, newValue) => {
            updateFilter("rating", newValue?.value || null);
          }}
          sx={styles.autocomplete("100px")}
          renderInput={(params) => <TextField {...params} placeholder={t("rating")} />}
        />
        <Autocomplete
          disablePortal
          disabled={isDataLoading}
          options={serviceAvailability}
          value={selectedAvailability}
          onChange={(_, newValue) => {
            updateFilter("availability", newValue?.value || null);
          }}
          sx={styles.autocomplete("150px")}
          renderInput={(params) => <TextField {...params} placeholder={t("availability")} />}
        />
        <Button variant="text-sm" onClick={clearAllFilters}>
          {t("clearAllFilters")}
        </Button>
      </Box>
    </Box>
  );
};

export default NetworkFilters;

const styles = {
  container: {
    width: "100%",
    overflowX: "auto",
    whiteSpace: "nowrap",
    padding: "0 8px 8px",
  },
  filtersWrapper: {
    display: "flex",
    gap: "10px",
    width: "fit-content",
  },
  autocomplete: (minWidth: string) => ({
    width: "fit-content",
    minWidth,
  }),
};
